import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer, toast } from 'react-toastify';
import styled, { ThemeProvider, keyframes } from 'styled-components';
import { HelmetProvider } from 'react-helmet-async';

// Context Providers
import { TravelContextProvider } from './contexts/TravelContext';
import { AuthProvider } from './contexts/AuthContext';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import BottomNavigation from './components/layout/BottomNavigation';
import ScrollToTop from './components/common/ScrollToTop';
import CookieConsent from './components/common/CookieConsent';

// Core pages (loaded eagerly)
import PlacesPage from './pages/PlacesPage';
import PlaceDetailPage from './pages/PlaceDetailPage';
import ItineraryPage from './pages/ItineraryPage';
import ItineraryExplorerPage from './pages/ItineraryExplorerPage';
import NotFoundPage from './pages/NotFoundPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProfilePage from './pages/ProfilePage';

// Styles
import 'react-toastify/dist/ReactToastify.css';
import GlobalStyles from './styles/GlobalStyles';
import { systemApi } from './services/api';
import { API_BASE_URL } from './config/environment';

// Lazy-loaded pages
const HeritageDestinationsPage = React.lazy(() => import('./pages/HeritageDestinationsPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const StonehengeGamePage = React.lazy(() => import('./pages/StonehengeGamePage'));
const SeasonalEventDetailPage = React.lazy(() => import('./pages/SeasonalEventDetailPage'));
const ChristmasMarketsPage = React.lazy(() => import('./pages/ChristmasMarketsPage'));
const CopyrightPage = React.lazy(() => import('./pages/CopyrightPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const PrivacyDashboardPage = React.lazy(() => import('./pages/PrivacyDashboardPage'));
const StandardizedPlaceContentPage = React.lazy(() => import('./pages/StandardizedPlaceContentPage'));
const DestinationDiscoveryPage = React.lazy(() => import('./pages/DestinationDiscoveryPage'));
const GuidePage = React.lazy(() => import('./pages/GuidePage'));
const GuidesIndexPage = React.lazy(() => import('./pages/GuidesIndexPage'));
const ComparisonPage = React.lazy(() => import('./pages/ComparisonPage'));
const PersonalityLandingPage = React.lazy(() => import('./pages/PersonalityLandingPage'));
const SeasonalLandingPage = React.lazy(() => import('./pages/SeasonalLandingPage'));

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Google-inspired bold & playful theme with unique personality
const theme = {
  colors: {
    // Primary brand - vibrant exploration colors
    primary: '#4285F4', // Google Blue
    primaryHover: '#3367D6',
    primaryLight: '#E8F0FE',
    primaryDark: '#1A73E8',
    
    // Secondary - warm, inviting colors
    secondary: '#34A853', // Google Green
    secondaryHover: '#2E7D32',
    secondaryLight: '#E6F4EA',
    
    // Accent colors - playful and vibrant
    accent: '#FBBC04', // Google Yellow
    accentHover: '#F9AB00',
    accentLight: '#FEF7E0',
    accentHero: '#FFF9E6', // Soft yellow for hero section
    
    // Tertiary - bold and energetic
    tertiary: '#EA4335', // Google Red
    tertiaryHover: '#C5221F',
    tertiaryLight: '#FCE8E6',
    
    // Purple for special features
    purple: '#9C27B0',
    purpleHover: '#7B1FA2',
    purpleLight: '#F3E5F5',
    
    // Semantic colors
    success: '#34A853',
    warning: '#FBBC04',
    error: '#EA4335',
    info: '#4285F4',
    
    // Neutral palette - warm and inviting
    background: '#FFFFFF',
    surface: '#FAFAFA',
    surfaceElevated: '#FFFFFF',
    text: '#202124', // Google's text color
    textSecondary: '#5F6368',
    textTertiary: '#9AA0A6',
    border: '#DADCE0',
    borderLight: '#E8EAED',
    
    // Interactive states
    focus: '#4285F4',
    hover: '#F8F9FA',
    disabled: '#9AA0A6',
    
    // Shadows - Material Design elevation
    shadow: 'rgba(60, 64, 67, 0.08)',
    shadowHover: 'rgba(60, 64, 67, 0.12)',
    shadowElevated: 'rgba(60, 64, 67, 0.16)',
    
    // Gradients
    gradientPrimary: 'linear-gradient(135deg, #4285F4 0%, #34A853 100%)',
    gradientSecondary: 'linear-gradient(135deg, #FBBC04 0%, #EA4335 100%)',
    gradientHero: 'linear-gradient(42deg, rgb(222 232 196) 0%, rgb(75 137 162) 50%, rgb(215 147 251) 100%)'
  },
  fonts: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    mono: '"SF Mono", "Monaco", "Cascadia Code", "Roboto Mono", "Courier New", monospace'
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px',
    ultrawide: '1600px'
  },
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    xxl: '3rem'      // 48px
  },
  fontSizes: {
    xs: '0.75rem',      // 12px - Labels, captions
    sm: '0.875rem',     // 14px - Small text, metadata
    base: '1rem',       // 16px - Body text (WCAG minimum)
    lg: '1.125rem',     // 18px - Large body, emphasized text
    xl: '1.25rem',      // 20px - Subheadings
    '2xl': '1.5rem',    // 24px - Section headings
    '3xl': '1.575rem',  // 30px - Page headings
    '4xl': '2.25rem',   // 36px - Hero headings
    '5xl': '3rem',      // 48px - Large hero headings
    '6xl': '3.75rem'    // 60px - Extra large hero
  },
  borderRadius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px'
  },
  minTouchTarget: '44px',
  transitions: {
    fast: '0.15s',
    normal: '0.2s',
    slow: '0.3s'
  }
};

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #000c1a;
`;

const MainContent = styled.main`
  flex: 1;
  padding-bottom: 80px; /* Account for bottom navigation on mobile */

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    padding-bottom: 0;
  }
`;

const FullscreenMainContent = styled.main`
  flex: 1;
`;

const loadingBarSlide = keyframes`
  0%   { transform: translateX(-100%); }
  50%  { transform: translateX(250%); }
  100% { transform: translateX(-100%); }
`;

const LoadingFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;

  &::after {
    content: '';
    width: 120px;
    height: 3px;
    background: #e8f0fe;
    border-radius: 3px;
    overflow: hidden;
    position: relative;
    display: block;
  }

  &::before {
    content: '';
    width: 48px;
    height: 3px;
    background: linear-gradient(90deg, #4285F4, #1A73E8);
    border-radius: 3px;
    animation: ${loadingBarSlide} 1.4s ease-in-out infinite;
  }
`;

const EXPLORER_ROUTES = ['/', '/explore'];

const SkipLink = styled.a`
  position: absolute;
  top: -40px;
  left: 6px;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 1000;
  font-weight: 600;

  &:focus {
    top: 6px;
  }
`;

function AppInner() {
  const location = useLocation();
  const isExplorer = EXPLORER_ROUTES.includes(location.pathname);

  return (
    <>
      <ScrollToTop />
      <AppContainer>
        <GlobalStyles />
        <SkipLink href="#main-content">Skip to main content</SkipLink>
        <Navbar />
        {isExplorer ? (
          <FullscreenMainContent id="main-content">
            <Routes>
              <Route path="/" element={<ItineraryExplorerPage />} />
              <Route path="/explore" element={<ItineraryExplorerPage />} />
            </Routes>
          </FullscreenMainContent>
        ) : (
          <MainContent id="main-content">
            <Suspense fallback={<LoadingFallback>Loading...</LoadingFallback>}>
            <Routes>
              <Route path="/places" element={<PlacesPage />} />
              <Route path="/places/:id" element={<PlaceDetailPage />} />
              <Route path="/heritage" element={<HeritageDestinationsPage />} />
              <Route path="/recommendations" element={<Navigate to="/itineraries" replace />} />
              <Route path="/discover" element={<DestinationDiscoveryPage />} />
              <Route path="/itinerary/:slug" element={<ItineraryPage />} />
              <Route path="/itineraries/:id?" element={<ItineraryPage />} />
              <Route path="/christmas-markets" element={<ChristmasMarketsPage />} />
              <Route path="/seasonal-events/:eventType/:eventId" element={<SeasonalEventDetailPage />} />
              <Route path="/saved" element={<PlacesPage saved={true} />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/game/stonehenge" element={<StonehengeGamePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/privacy/dashboard" element={<PrivacyDashboardPage />} />
              <Route path="/standardized-place-content/:placeName" element={<StandardizedPlaceContentPage />} />
              <Route path="/standardized-place-content/id/:placeId" element={<StandardizedPlaceContentPage />} />
              <Route path="/copyright" element={<CopyrightPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/guides" element={<GuidesIndexPage />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
              <Route path="/compare/:country" element={<ComparisonPage />} />
              <Route path="/travel-style/:personality" element={<PersonalityLandingPage />} />
              <Route path="/travel/:season" element={<SeasonalLandingPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </MainContent>
        )}
        <Footer />
        <BottomNavigation />
        <CookieConsent />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </AppContainer>
    </>
  );
}

function App() {
  // Handle OAuth redirect tokens in URL fragment (Supabase fallback)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token=')) {
      // Extract access_token from URL fragment
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const error = hashParams.get('error');

      if (error) {
        toast.error('Authentication failed. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      if (accessToken) {
        // Send token to backend for processing
        const callbackUrl = `${API_BASE_URL}/auth/callback/google?access_token=${encodeURIComponent(accessToken)}`;

        // Clean URL immediately
        window.history.replaceState({}, document.title, window.location.pathname);

        // Redirect to backend for token exchange
        window.location.href = callbackUrl;
      }
    }
  }, []);

  useEffect(() => {
    systemApi.health()
      .then(res => {
        // Simple visibility into backend availability during development
        if (process.env.NODE_ENV === 'development') {
          console.log('Backend health:', res.data?.status || 'unknown');
        }
      })
      .catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Backend health check failed:', err?.message || err);
        }
        toast.error('Backend unavailable. Some features may not work.', {
          toastId: 'backend-down',
          autoClose: false,
          closeOnClick: true,
        });
      });
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <TravelContextProvider>
            <AuthProvider>
              <Router>
                <AppInner />
              </Router>
            </AuthProvider>
        </TravelContextProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
